import React from 'react';
import { ArrowLeft, Calendar, Clock, Info, Users, X } from 'lucide-react';

// Extracted from StudentDashboard.js — session details modal + phone display helper.

const normalizePhoneForDisplay = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return phone || '';
};

const DetailsModal = ({ data, onClose, onRequestRefund }) => {
  if (!data) return null;

  // Calculate duration display
  const durationText = data.duration_value && data.duration_unit
    ? `${data.duration_value} ${data.duration_unit}`
    : '60 Mins';

  // Calculate price display
  const priceDisplay = data.proposed_amount || data.price || 0;

  // Determine session status badge
  const getStatusBadge = () => {
    if (data.payment_status === 'HELD' || data.payment_status === 'ENROLLED') {
      return { text: 'Active', color: 'var(--cyan)', bg: 'var(--blue-dim)' };
    }
    if (data.is_completed || data.payment_status === 'RELEASED') {
      return { text: 'Completed', color: 'var(--green)', bg: 'var(--green-dim)' };
    }
    return { text: 'Available', color: 'var(--proto-white)', bg: 'var(--bg-soft)' };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="stu-modal-overlay" onClick={onClose} style={{
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div className="session-details-modal" onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--graphite-dark)',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        animation: 'modalSlideIn 0.3s ease-out',
        margin: 'auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--bg-soft)',
          padding: '24px',
          borderBottom: '1px solid var(--border)',
          position: 'relative'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <span style={{
              fontSize: '13px',
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>Session Details</span>
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--proto-white)',
            margin: 0
          }}>Complete session information and booking</h3>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              color: 'white'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '28px' }}>
          {/* Title and Status */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--proto-white)',
                margin: 0,
                lineHeight: 1.3,
                flex: 1
              }}>{data.title}</h2>
              <span style={{
                background: statusBadge.bg,
                color: statusBadge.color,
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}>{statusBadge.text}</span>
            </div>
          </div>

          {/* Professor Info Card */}
          <div style={{
            background: 'var(--engine-black)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '18px',
                fontWeight: 700,
                flexShrink: 0
              }}>
                {data.professor_name ? data.professor_name.charAt(0).toUpperCase() : 'SC'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'var(--proto-white)',
                  marginBottom: '2px'
                }}>
                  {data.professor_name ? `Dr. ${data.professor_name}` : 'Dr. Sarah Chen'}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  fontWeight: 500
                }}>Instructor</div>
              </div>
            </div>
          </div>

          {/* Session Type Badge */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: data.session_type === 'GROUP' ? 'var(--blue-dim)' : 'var(--bg-soft)',
              color: data.session_type === 'GROUP' ? 'var(--cyan)' : 'var(--proto-white)',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              border: '1px solid var(--border)'
            }}>
              {data.session_type === 'GROUP' ? <Users size={14} /> : data.domain ? '' : '💻'}
              {data.session_type || data.domain || 'workshop'}
              {data.meeting_link && data.meeting_link.includes('zoom') && (
                <>
                  <span style={{ margin: '0 4px', opacity: 0.5 }}>•</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    📹 Online (Zoom)
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ gridColumn: '1 / -1' }}>
              {window.renderSessionScheduleHelper ? window.renderSessionScheduleHelper(data) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Date */}
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Calendar size={12} /> Date
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--proto-white)' }}>
                      {new Date(data.event_date.split('T')[0]).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  {/* Time */}
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={12} /> Time
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--proto-white)' }}>
                      {data.start_time} PST
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Duration */}
            <div>
              <div style={{
                fontSize: '11px',
                color: 'var(--text-faint)',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: '0.5px',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                ⏱️ Duration
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--proto-white)'
              }}>
                {durationText}
              </div>
            </div>


            {/* Availability */}
            {data.participant_count !== undefined && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  fontSize: '11px',
                  color: 'var(--text-faint)',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  marginBottom: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Users size={12} /> Availability
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--proto-white)'
                }}>
                  {data.max_participants ? (
                    <>
                      {data.participant_count >= data.max_participants ? (
                        <span style={{ color: '#dc2626' }}>
                          Session Full • {data.participant_count}/{data.max_participants} enrolled
                        </span>
                      ) : (
                        <>
                          {data.participant_count || 0} enrolled •
                          <span style={{ color: '#16a34a', marginLeft: '4px' }}>
                            {data.max_participants - (data.participant_count || 0)} spots left
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    `${data.participant_count || 0} enrolled • Spots available`
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {data.description && (
            <div style={{
              background: 'var(--engine-black)',
              padding: '14px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid var(--border)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                marginBottom: '6px'
              }}>Description</div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
              }}>
                {data.description}
              </div>
            </div>
          )}

          {/* Topics Covered */}
          {data.topics_covered && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Topics Covered
              </div>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '13px',
                color: 'var(--text-muted)',
                lineHeight: 1.8
              }}>
                {data.topics_covered.split(',').map((topic, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{topic.trim()}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Prerequisites */}
          {data.prerequisites && (
            <div style={{
              background: 'var(--bg-soft)',
              padding: '14px 16px',
              borderRadius: '10px',
              marginBottom: '20px',
              borderLeft: '4px solid #f59e0b',
              border: '1px solid var(--border)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#f59e0b',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚠️ Prerequisites
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
                fontWeight: 500
              }}>{data.prerequisites}</div>
            </div>
          )}

          {/* Materials */}
          {(data.materials_slides || data.materials_documents) && (
            <div style={{
              background: 'var(--green-dim)',
              padding: '14px 16px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid var(--border)',
              borderLeft: '4px solid var(--green)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--green)',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                📦 Materials
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--proto-white)',
                fontWeight: 500
              }}>
                {data.materials_slides && data.materials_documents
                  ? 'Slides and code examples will be provided'
                  : data.materials_slides
                    ? 'Slides will be provided'
                    : 'Code examples will be provided'}
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div style={{
            background: 'var(--blue-dim)',
            padding: '14px 16px',
            borderRadius: '10px',
            borderLeft: '4px solid var(--vector-blue)',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start'
          }}>
            <Info size={16} style={{ color: 'var(--vector-blue)', flexShrink: 0, marginTop: '2px' }} />
            <p style={{
              fontSize: '13px',
              color: 'var(--cyan)',
              margin: 0,
              lineHeight: 1.5,
              fontWeight: 500
            }}>
              This is a premium session verified by Compliance. Attendance will be recorded for university credits.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          background: 'var(--engine-black)',
          borderTop: '1px solid var(--border)'
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, var(--cyan) 0%, var(--vector-blue) 100%)',
              color: 'var(--engine-black)',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 800,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(25, 211, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.3)';
            }}
          >
            Got it
          </button>

          {(data.payment_status === 'SUCCESS' || data.payment_status === 'HELD' || data.payment_status === 'ENROLLED' || data.payment_status === 'RELEASED' || data.payment_status === 'ESCROWED') && data.payment_id && (
            <button
              onClick={() => onRequestRefund(data)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                background: 'transparent',
                color: '#ef4444',
                border: '1px solid #fecaca',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#fef2f2';
                e.target.style.borderColor = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = '#fecaca';
              }}
            >
              <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Request Refund
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- MAIN DASHBOARD COMPONENT --- */

export { DetailsModal, normalizePhoneForDisplay };
